import React, { useState, useEffect } from 'react';
import { SavedConnection, StreamingPlatform } from '../../types/streaming';
import { Trash2 } from 'lucide-react';
import PlatformIcon from './PlatformIcon';
import { FILL_GRADIENT } from '../../constants/gradients';

const STORAGE_KEY = 'beemi-saved-connections';
const DISCORD_SHARE_KEY = 'beemi-share-to-discord';

const platformColors: Record<StreamingPlatform, string> = {
  tiktok: '#00f2ea',
  youtube: '#FF0000',
  twitch: '#9146FF'
};

const platformIcons: Record<StreamingPlatform, string> = {
  tiktok: '/vectors/tik-tok.svg',
  youtube: '/vectors/youtube.svg',
  twitch: '/vectors/twitch.svg'
};

interface SavedConnectionsProps {
  onSelect: (connection: SavedConnection) => void;
  onAddNew: () => void;
  disabled?: boolean;
}

export const SavedConnections: React.FC<SavedConnectionsProps> = ({
  onSelect,
  onAddNew,
  disabled = false
}) => {
  const [connections, setConnections] = useState<SavedConnection[]>([]);
  const [isRemoving, setIsRemoving] = useState(false);
  const [shareToDiscord, setShareToDiscord] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(DISCORD_SHARE_KEY);
      return stored === null ? true : stored === 'true';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    loadConnections();
  }, []);

  const loadConnections = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setConnections(parsed);
      }
    } catch (error) {
      console.error('Failed to load saved connections:', error);
    }
  };

  const toggleDiscordShare = () => {
    const next = !shareToDiscord;
    setShareToDiscord(next);
    try {
      localStorage.setItem(DISCORD_SHARE_KEY, String(next));
    } catch {
      // ignore
    }
  };

  const removeConnection = (id: string) => {
    const updated = connections.filter(c => c.id !== id);
    setConnections(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  };

  if (connections.length === 0) {
    return (
      <div className="text-center py-8">
        <span className="text-4xl mb-4 block">📡</span>
        <p className="text-gray-400 mb-4">No saved connections</p>
        <button
          onClick={onAddNew}
          disabled={disabled}
          className="px-6 py-3 bg-gradient-to-r from-pink-500 to-yellow-500 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          Add Connection
        </button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <h3 className="text-sm font-semibold text-white">Recent Connections</h3>
        <button
          onClick={() => setIsRemoving(!isRemoving)}
          className="text-xs text-gray-400 hover:text-white transition-colors"
        >
          {isRemoving ? 'Done' : 'Edit'}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto pr-1 space-y-1.5 min-h-0">
        {connections.map((connection) => (
          <button
            key={connection.id}
            onClick={() => !isRemoving && !disabled && onSelect(connection)}
            disabled={disabled}
            className={`
              w-full px-3 py-2.5 flex items-center gap-3 transition-all
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              bg-gray-800 hover:bg-gray-700
            `}
            style={{ borderLeft: `3px solid ${platformColors[connection.platform]}` }}
          >
            {connection.profilePicture ? (
              <div className="relative flex-shrink-0">
                <img
                  src={connection.profilePicture}
                  alt={connection.username}
                  className="w-7 h-7 rounded-full object-cover"
                  style={{ border: `1.5px solid ${platformColors[connection.platform]}` }}
                />
                <div
                  className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: platformColors[connection.platform] }}
                >
                  <PlatformIcon platform={connection.platform} className="w-2 h-2 text-white" />
                </div>
              </div>
            ) : (
              <PlatformIcon platform={connection.platform} className="w-4 h-4 text-white flex-shrink-0" />
            )}
            <div className="flex-1 text-left min-w-0">
              <div className="text-xs font-medium text-white truncate">@{connection.username}</div>
              <div className="text-gray-400 capitalize" style={{ fontSize: '10px' }}>{connection.platform}</div>
            </div>
            {isRemoving ? (
              <button
                onClick={(e) => { e.stopPropagation(); removeConnection(connection.id); }}
                className="w-6 h-6 rounded bg-red-500/20 text-red-400 flex items-center justify-center hover:bg-red-500 hover:text-white transition-colors flex-shrink-0"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            ) : (
              <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            )}
          </button>
        ))}
      </div>

      <button
        onClick={onAddNew}
        disabled={disabled}
        className="flex-shrink-0 mt-3 w-full py-2.5 rounded-lg border border-dashed border-gray-600 text-gray-400 hover:border-gray-500 hover:text-gray-300 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50 text-xs"
      >
        <span>+</span>
        <span>Add New Connection</span>
      </button>

      <div className="flex-shrink-0 mt-3 pt-3 border-t border-gray-700/60">
        <button
          onClick={toggleDiscordShare}
          className="w-full flex items-center justify-between gap-3 group relative"
        >
          <div className="absolute bottom-full left-0 right-0 mb-2 px-2.5 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-10" style={{ fontSize: '10px' }}>
            Your stream will be announced in the stream channel of the Beemi Discord and a link to your stream will be posted.
          </div>
          <div className="flex items-center gap-2 min-w-0">
            <svg className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.015.04.033.05a19.834 19.834 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
            </svg>
            <span className="text-xs text-gray-300 truncate">
              Share stream to Beemi discord
            </span>
          </div>
          <div
            className="relative flex-shrink-0 w-8 h-4 rounded-full transition-all duration-200"
            style={{ background: shareToDiscord ? FILL_GRADIENT : '#4B5563' }}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform duration-200 ${
                shareToDiscord ? 'translate-x-4' : 'translate-x-0'
              }`}
            />
          </div>
        </button>
      </div>
    </div>
  );
};

// Helper function to save a connection
export const saveConnection = (
  platform: StreamingPlatform,
  username: string,
  profilePicture?: string
) => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const connections: SavedConnection[] = stored ? JSON.parse(stored) : [];
    
    // Check if already exists
    const existingIndex = connections.findIndex(
      c => c.platform === platform && c.username.toLowerCase() === username.toLowerCase()
    );

    const connection: SavedConnection = {
      id: existingIndex >= 0 ? connections[existingIndex].id : `${platform}_${username}_${Date.now()}`,
      platform,
      username,
      profilePicture,
      addedAt: existingIndex >= 0 ? connections[existingIndex].addedAt : new Date(),
      lastUsedAt: new Date()
    };

    if (existingIndex >= 0) {
      connections[existingIndex] = connection;
    } else {
      connections.unshift(connection);
    }

    // Keep only last 10 connections
    const trimmed = connections.slice(0, 10);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch (error) {
    console.error('Failed to save connection:', error);
  }
};

export default SavedConnections;

